"use client";

import posthog from "posthog-js";
import { reportGoogleAdsLead } from "@/lib/analytics/google-ads";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    // OpenAI / ChatGPT Ads pixel, installed in both [lang] layouts.
    oaiq?: (...args: unknown[]) => void;
  }
}

// ChatGPT Ads conversions. The pixel's call shape is
// oaiq("measure", "<event>", { type: "<category>" }) - the category is fixed
// per event by the SDK (lead_created and appointment_scheduled are both
// "customer_action"), and the Ads Manager UI generates exactly this snippet.
const OPENAI_EVENT_TYPE = 'customer_action';

function reportOpenAIConversion(event: string): void {
  window.oaiq?.('measure', event, { type: OPENAI_EVENT_TYPE });
}

// PostHog events that are also a conversion, mapped to the pixel's event
// vocabulary. Only events that fire AFTER a confirmed success belong here -
// `booking_submitted` fires on the attempt, so the booking conversion is
// reported explicitly from the response.ok branch in
// components/booking/booking.tsx instead.
const OPENAI_CONVERSIONS: Record<string, string> = {
  lead_form_submitted: 'lead_created',
};

// PostHog events that are a Google Ads lead. The landing-page and persona
// forms only report through these events, so without this they never reached
// Google Ads. Every lead_form_submitted fires only after the server accepted
// the lead. AI Blueprint reports from its /api/lead response instead, because
// ai_blueprint_lead_submitted fires before the request goes out.
const GOOGLE_ADS_LEAD_EVENTS = new Set(['lead_form_submitted']);

type IdentifyProps = {
  phone?: string;
  name?: string;
  lead_source?: string;
  [key: string]: unknown;
};

// posthog-js buffers events fired before init completes and flushes them on
// loaded, so we don't gate calls - only short-circuit on the server.
const isBrowser = (): boolean => typeof window !== "undefined";

export function useAnalytics() {
  const trackEvent = (
    event: string,
    properties?: Record<string, unknown>
  ): void => {
    if (!isBrowser()) return;
    posthog.capture(event, properties);
    const openAiEvent = OPENAI_CONVERSIONS[event];
    if (openAiEvent) reportOpenAIConversion(openAiEvent);
    if (GOOGLE_ADS_LEAD_EVENTS.has(event)) reportGoogleAdsLead();
  };

  // Conversions that can't be derived from a PostHog event name alone (the
  // booking, which is only real once the server confirms it).
  const trackConversion = (event: string): void => {
    if (!isBrowser()) return;
    reportOpenAIConversion(event);
  };

  // WhatsApp clicks are a conversion (like booking): reported to Meta Pixel as
  // the custom event "WhatsAppClick" and to PostHog as "whatsapp_click".
  const trackWhatsAppClick = (
    properties?: Record<string, unknown>
  ): void => {
    if (!isBrowser()) return;
    posthog.capture("whatsapp_click", properties);
    window.fbq?.("trackCustom", "WhatsAppClick", properties);
  };

  const identifyLead = (email: string, props: IdentifyProps = {}): void => {
    if (!isBrowser() || !email) return;
    const cleaned: Record<string, unknown> = { email };
    for (const [k, v] of Object.entries(props)) {
      if (v !== undefined && v !== null && v !== "") cleaned[k] = v;
    }
    posthog.identify(email, cleaned);
  };

  const resetIdentity = (): void => {
    if (!isBrowser()) return;
    posthog.reset();
  };

  return { trackEvent, trackConversion, trackWhatsAppClick, identifyLead, resetIdentity };
}
